import {
  Box,
  Heading,
  Text,
  HStack,
  Button,
  IconButton,
  Menu,
  Badge,
  Wrap,
  Portal,
} from '@chakra-ui/react';
import { FiEdit2, FiRefreshCw, FiMap, FiMoreVertical, FiMail } from 'react-icons/fi'
import { Tooltip } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom'
import type { ListOverview } from '../../types/contactList'
import { canManuallyRefresh, isAutoDaily } from '../../lib/listRefresh'

interface Props {
  overview: ListOverview
  onRefresh: () => void
  onViewMap: () => void
  onTagUniverse: () => void
  onUseInCampaign: () => void
  /** True while a materialize job (enqueue or poll) is in flight (slice 6b, WI-42). */
  isRefreshing?: boolean
}

export function OverviewHeader({ overview, onRefresh, onViewMap, onTagUniverse, onUseInCampaign, isRefreshing }: Props) {
  const navigate = useNavigate()
  return (
    <Box>
      <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">{overview.label}</Heading>
          {overview.description && <Text color="gray.500" mt={1}>{overview.description}</Text>}
        </Box>
        <Wrap gap={2}>
          <Button
            colorPalette="brand"
            onClick={() => navigate(`/dashboard/lists/${overview.id}/edit`)}><FiEdit2 />Edit composition
                      </Button>
          {canManuallyRefresh(overview) && (
            <Button variant="outline" onClick={onRefresh} loading={isRefreshing}><FiRefreshCw />Refresh</Button>
          )}
          <Button variant="outline" onClick={onViewMap}><FiMap />View on map</Button>
          <Tooltip content="Materialize the list first" disabled={overview.isMaterialized}>
            <Box as="span" display="inline-block">
              <Button
                variant="outline"
                disabled={!overview.isMaterialized}
                onClick={() => {
                  const returnTo = encodeURIComponent(`/dashboard/lists/${overview.id}`)
                  const returnLabel = encodeURIComponent('Contact list')
                  navigate(
                    `/dashboard/communications/email/new?source=contactList&id=${overview.id}&returnTo=${returnTo}&returnLabel=${returnLabel}`,
                  )
                }}
              ><FiMail />Email list</Button>
            </Box>
          </Tooltip>
          <Menu.Root>
            <Menu.Trigger asChild><IconButton aria-label="More actions" variant="outline"><FiMoreVertical /></IconButton></Menu.Trigger>
            <Portal><Menu.Positioner><Menu.Content>
                  <Menu.Item onSelect={onTagUniverse} value='item-0'>Tag universe</Menu.Item>
                  <Menu.Item onSelect={onUseInCampaign} value='item-1'>Use in campaign</Menu.Item>
                </Menu.Content></Menu.Positioner></Portal>
          </Menu.Root>
        </Wrap>
      </HStack>
      <HStack mt={4} gap={4} flexWrap="wrap">
        <StatChip label="Contacts" value={overview.memberCount.toLocaleString()} />
        <StatChip label={`Registered · ${pct(overview.registeredCount, overview.memberCount)}`} value={overview.registeredCount.toLocaleString()} />
        <StatChip label="Union households" value={overview.union.memberCount.toLocaleString()} />
        <Badge colorPalette={overview.isMaterialized ? 'green' : 'purple'}>
          {overview.isMaterialized ? 'Materialized' : overview.refreshStrategy}
        </Badge>
        {isAutoDaily(overview) && <Text fontSize="xs" color="ink.2">Refreshes automatically each day</Text>}
        <Text fontSize="sm" color="gray.500">
          {overview.lastRefreshed ? `Refreshed ${new Date(overview.lastRefreshed).toLocaleDateString()}` : 'Never refreshed'}
        </Text>
      </HStack>
    </Box>
  );
}

function pct(n: number, total: number) { return total > 0 ? `${Math.round((100 * n) / total)}%` : '0%' }
function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text fontSize="xl" fontWeight="bold" lineHeight="1.1">{value}</Text>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">{label}</Text>
    </Box>
  )
}
